/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCosmosComponentContext } from "@components/context";
import { OptimizedCosmosAssetFragment } from "@components/elements";
import { getHygraphImageIds, isHygraphImageSrc } from "@components/utils";

/**
 * Satisfies the next/image loader.  Adds the ?width param to satisfy the check for optimized.
 * The is the default loader.
 */

const hygraphImageLoader = (hygraphMediaDomain: string) => {
	return ({ src, width }: { src?: string; width?: number }): string => {
		if (!src) return "";

		//check for hygraph assets
		if (isHygraphImageSrc(hygraphMediaDomain, src)) {
			if (src.includes("resize=width") && src.includes("output=format")) {
				//already specified the correct optimized width and format

				//try to append ?width param for next-image
				if (!src.includes("?width=") && !src.includes("&width=")) {
					const matches = src.match(/\/resize=width:(\d*)\//);
					if (matches && matches.length && matches[1]) {
						const width = matches[1];
						if (width) {
							const url = new URL(src);
							url.searchParams.set("width", width);
							return url.toString();
						}
					}
				}

				return src;
			}
		} else {
			//try configure URL for Akamai Image Manager on cdn2
			if (width) {
				try {
					const url = new URL(src);

					//make adjustments for CMS cdn2
					if (url.hostname === "cdn2.unrealengine.com") {
						//use the w param instead of the width
						const paramWidth = url.searchParams.get("w");

						//force akamai url params to optimze images on cdn2
						url.searchParams.set("resize", "1");
						url.searchParams.set("w", `${paramWidth || width}`);
						url.searchParams.set("width", `${paramWidth || width}`);
					} else {
						url.searchParams.set("width", `${width}`);
					}
					return url.toString();
				} catch {
					console.warn("Could not parse image src to append width");
				}
			}
			return src;
		}

		const { assetId, environmentId } = getHygraphImageIds(hygraphMediaDomain, src);
		if (!assetId) return "";

		const envPartStr = environmentId ? `/${environmentId}` : "";

		if (!width) {
			//the width url param makes next happy
			return `https://${hygraphMediaDomain}/${assetId}/output=format:webp${envPartStr}?width=${0}`;
		}

		return `https://${hygraphMediaDomain}/${assetId}/resize=width:${width}/output=format:webp${envPartStr}?width=${width}`;
	};
};

/**
 * Does not run the image transformation and still satisfies the check for optimized.
 * This should be used for svg files since they can't be transformed.
 */
const disabledHygraphImageLoader = (hygraphMediaDomain: string) => {
	return ({ src, width }: { src?: string; width?: number }): string => {
		if (!src) return "";

		//Only try for hygraph assets
		if (!isHygraphImageSrc(hygraphMediaDomain, src)) return src;

		const { assetId, environmentId } = getHygraphImageIds(hygraphMediaDomain, src);
		if (!assetId) return "";

		const envPartStr = environmentId ? `/${environmentId}` : "";

		return `https://${hygraphMediaDomain}/${assetId}${envPartStr}?width=${width}`;
	};
};

export const useHygraphImageLoader = (): ReturnType<typeof hygraphImageLoader> => {
	const { hygraphMediaDomain } = useCosmosComponentContext();
	return hygraphImageLoader(hygraphMediaDomain);
};

export const useAssetOptUrl = (asset: OptimizedCosmosAssetFragment | null | undefined): string => {
	const imageLoader = useHygraphImageLoader();
	if (!asset) return "";
	if (asset.optUrl) return asset.optUrl;

	const optUrl = imageLoader({ src: asset.url, width: asset.width || undefined });
	return optUrl;
};

export const useDisabledHygraphImageLoader = (): ReturnType<typeof disabledHygraphImageLoader> => {
	const { hygraphMediaDomain } = useCosmosComponentContext();
	return disabledHygraphImageLoader(hygraphMediaDomain);
};
